import React, { useState, useEffect } from 'react';
import { EXPORT_COUNTRIES } from '../mock/mock';

const MAP_ASPECT_H = 65.26;  // matches 620/950 viewBox of world-map.svg
const PER_ARROW_MS = 1800;   // gap between each arrow launch
const HOLD_COMPLETE_MS = 3500; // hold once all covered
const DESTINATIONS = EXPORT_COUNTRIES.filter(c => !c.home);
const HOME = EXPORT_COUNTRIES.find(c => c.home);

const ExportArrowsMap = () => {
  const [landedCount, setLandedCount] = useState(0);
  const [cycle, setCycle] = useState(0); // increments each reset for fresh animations

  // Progressive reveal → hold → reset → loop
  useEffect(() => {
    if (landedCount < DESTINATIONS.length) {
      const t = setTimeout(() => setLandedCount(c => c + 1), PER_ARROW_MS);
      return () => clearTimeout(t);
    }
    // All covered → hold, then reset
    const t = setTimeout(() => {
      setCycle(c => c + 1);
      setLandedCount(0);
    }, HOLD_COMPLETE_MS);
    return () => clearTimeout(t);
  }, [landedCount]);

  const allCovered = landedCount >= DESTINATIONS.length;

  // Build routes from Jaipur → each destination
  const routes = DESTINATIONS.map((c) => {
    const x1 = HOME.x;
    const y1 = HOME.y * MAP_ASPECT_H / 100;
    const x2 = c.x;
    const y2 = c.y * MAP_ASPECT_H / 100;
    const cx = (x1 + x2) / 2;
    const cy = Math.min(y1, y2) - 12;
    return { c, d: `M ${x1} ${y1} Q ${cx} ${cy} ${x2} ${y2}` };
  });

  return (
    <div className="relative w-full" style={{ paddingBottom: `${MAP_ASPECT_H}%` }}>
      {/* Real geographic world map backdrop */}
      <img
        src="/world-map.svg"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover opacity-40"
        style={{ filter: 'sepia(0.35) brightness(0.95) contrast(0.85)' }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-[#faf6f0]/40 via-transparent to-[#e8dfd1]/30 pointer-events-none" />

      {/* Persistent arrows (accumulate as they land) */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox={`0 0 100 ${MAP_ASPECT_H}`}
        preserveAspectRatio="none"
      >
        <defs>
          <marker
            id="rcd-persist-arrow"
            markerWidth="6"
            markerHeight="6"
            refX="5"
            refY="3"
            orient="auto"
          >
            <path d="M0,0 L6,3 L0,6 Z" fill="#a67c3d" />
          </marker>
        </defs>
        {routes.map((r, i) =>
          i < landedCount ? (
            <path
              key={`${cycle}-arrow-${i}`}
              d={r.d}
              stroke="#a67c3d"
              strokeWidth="0.35"
              fill="none"
              strokeLinecap="round"
              markerEnd="url(#rcd-persist-arrow)"
              className="rcd-arrow-draw"
              style={{ opacity: 0.85 }}
            />
          ) : null
        )}
      </svg>

      {/* Leader lines from marker → tag (only when covered) */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox={`0 0 100 ${MAP_ASPECT_H}`}
        preserveAspectRatio="none"
      >
        {routes.map((r, i) => {
          if (i >= landedCount) return null;
          const c = r.c;
          const tagX = c.tagX ?? c.x;
          const tagY = (c.tagY ?? c.y - 4);
          return (
            <line
              key={`${cycle}-leader-${i}`}
              x1={c.x}
              y1={(c.y * MAP_ASPECT_H) / 100}
              x2={tagX}
              y2={(tagY * MAP_ASPECT_H) / 100}
              stroke="#a67c3d"
              strokeWidth="0.15"
              strokeDasharray="0.6 0.6"
              opacity="0.7"
              className="rcd-leader-in"
            />
          );
        })}
      </svg>

      {/* Destination markers — appear one by one */}
      {routes.map((r, i) => {
        const covered = i < landedCount;
        if (!covered) return null;
        const isLatest = i === landedCount - 1;
        const c = r.c;
        return (
          <div
            key={`${cycle}-marker-${i}`}
            className="absolute -translate-x-1/2 -translate-y-1/2 rcd-marker-pop"
            style={{ left: `${c.x}%`, top: `${c.y}%`, zIndex: 5 }}
          >
            {/* Landing burst */}
            {isLatest && <span className="rcd-marker-burst" />}
            {/* Marker */}
            <span
              className={`relative block rounded-full ring-2 ring-[#faf6f0] ${
                allCovered ? 'rcd-marker-final' : ''
              }`}
              style={{
                width: allCovered ? 14 : 12,
                height: allCovered ? 14 : 12,
                background: '#a67c3d',
                boxShadow: allCovered
                  ? '0 0 0 4px rgba(166,124,61,0.25), 0 4px 10px -2px rgba(26,22,20,0.3)'
                  : '0 2px 4px rgba(26,22,20,0.25)'
              }}
            />
          </div>
        );
      })}

      {/* Country name tags — positioned independently so every name stays visible */}
      {routes.map((r, i) => {
        if (i >= landedCount) return null;
        const c = r.c;
        const tagX = c.tagX ?? c.x;
        const tagY = c.tagY ?? c.y - 4;
        return (
          <div
            key={`${cycle}-tag-${i}`}
            className={`absolute -translate-x-1/2 -translate-y-1/2 rcd-tag-float ${
              allCovered ? 'rcd-tag-float--final' : ''
            }`}
            style={{ left: `${tagX}%`, top: `${tagY}%`, zIndex: 7 }}
          >
            <img
              src={c.flag}
              alt={`${c.short} flag`}
              className="w-5 h-3.5 md:w-6 md:h-4 object-cover border border-[#e0d6c5] flex-shrink-0"
            />
            <span>{c.short}</span>
          </div>
        );
      })}

      {/* Jaipur HQ marker */}
      <div
        className="absolute -translate-x-1/2 -translate-y-1/2"
        style={{ left: `${HOME.x}%`, top: `${HOME.y}%`, zIndex: 6 }}
      >
        <span className="absolute w-14 h-14 -translate-x-1/2 -translate-y-1/2 left-1/2 top-1/2 rounded-full bg-[#a67c3d]/25 animate-ping" />
        <span className="absolute w-8 h-8 -translate-x-1/2 -translate-y-1/2 left-1/2 top-1/2 rounded-full bg-[#a67c3d]/40" />
        <span className="relative block w-4 h-4 rounded-full bg-[#a67c3d] ring-2 ring-[#faf6f0] shadow-lg" />
        <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 whitespace-nowrap bg-[#1a1614] text-[#faf6f0] text-[9px] tracking-[0.24em] uppercase px-2.5 py-1 shadow-md">
          Jaipur · HQ
        </div>
      </div>

      {/* Progress indicator */}
      <div className="absolute bottom-2 left-2 md:bottom-4 md:left-4 flex items-center gap-2 bg-[#faf6f0]/85 backdrop-blur px-3 py-1.5 shadow-sm">
        <span className="text-[9px] md:text-[10px] tracking-[0.24em] uppercase text-[#6b5847]">
          {allCovered ? 'All markets served' : `${landedCount} / ${DESTINATIONS.length} covered`}
        </span>
        <div className="flex gap-1">
          {DESTINATIONS.map((_, i) => (
            <span
              key={i}
              className={`w-1.5 h-1.5 rounded-full transition-colors duration-500 ${
                i < landedCount ? 'bg-[#a67c3d]' : 'bg-[#c9bfae]'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ExportArrowsMap;
